import React, { useState, useEffect, useMemo, useRef } from "react";
import Cookies from "js-cookie";
import { setSessionCookie } from "./helpers/session";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, ScaleControl, useMapEvents } from "react-leaflet";
import ReactMarkdown from "react-markdown";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import "react-leaflet-cluster/dist/assets/MarkerCluster.css";
import "react-leaflet-cluster/dist/assets/MarkerCluster.Default.css";
import "./App.css";
import "./mobile.css";
import L from 'leaflet';

import { boatIcon, homeIcon, yardIcon } from "./constants/MapIcons";
import { mapStyles } from "./constants/mapStyles";

import FlyToPosition from "./components/FlyToPosition";
import ImageCarousel from "./components/ImageCarousel";
import LoginWidget from "./components/LoginWidget";
import SignupWidget from "./components/SignupWidget";
import SubmitWidget from "./components/SubmitWidget";
import MySubmissions from "./components/MySubmissions";
import SettingsWidget from "./components/SettingsWidget";
import FavoritesPanel from "./components/FavoritesPanel";
import SidebarCarousel from "./components/SidebarCarousel";
import ProfileCard from "./components/ProfileCard";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsAndConditions from "./components/TermsAndConditions";
import AboutPage from "./components/AboutPage";
import ContactPage from "./components/ContactPage";
import PendingPanel from "./components/PendingPanel";
import UserAdmin from "./components/UserAdmin";
import SearchBar from "./components/SearchBar";
import MapTools from "./components/MapTools";
import MobileNotice from "./components/MobileNotice";
import Leaderboard from "./components/Leaderboard";
import ShipDetails from "./components/ShipDetails";
import UserProfile from "./components/UserProfile";
import UserLink from "./components/UserLink";
import RecentChanges from "./components/RecentChanges";
import FilterPanel from "./components/FilterPanel";
import TimelineBar from "./components/TimelineBar";
import NotificationsPanel from "./components/NotificationsPanel";
import ReportDialog from "./components/ReportDialog";
import NearbyPanel from "./components/NearbyPanel";

import useShips from "./hooks/useShips";
import useAuth from "./hooks/useAuth";
import useTheme from "./hooks/useTheme";
import useMapTools from "./hooks/useMapTools";
import useShipFilters from "./hooks/useShipFilters";
import useNotifications from "./hooks/useNotifications";
import {
  MapBridge, MeasureLayer, CoordinatesTracker, PinMarker, RadiusLayer,
  MeasurePanel, CoordinatesBar, GoToBox, RadiusPanel,
} from "./components/MapExtras";

import { formatLatLng } from "./helpers/geo";
import { parseCoords, formatCoords } from "./helpers/coordinates";
import { buildSlugs, shipRefFromPath, findShipByRef, shareUrl, userFromPath, userPath, changesFromPath, changesPath, changesShareUrl } from "./helpers/slug";
import { preview } from "./helpers/description";
import { needsWorkReasons } from "./helpers/shipFilters";
import { pickRandomShip } from "./helpers/nearby";

// Same origin: Cloudflare Pages Functions serve /api/*. Set REACT_APP_API_BASE to override.
const API_BASE = process.env.REACT_APP_API_BASE || "";


const EMPTY_SUBMIT = {
  title: "", coords: "", imo: "", date: "", owner: "", port_of_registry: "", type: "",
  tonnage: "", draught: "", length: "", beam: "", capacity: "", crew: "",
  builder: "", description: "", images: "", image_meta: {}, photo_attribution: "", links: "",
};
const DRAFT_KEY = "open-wrecks:submit-draft";

const loadDraft = () => {
  try {
    const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
    return d && typeof d === "object" ? { ...EMPTY_SUBMIT, ...d } : null;
  } catch {
    return null;
  }
};

// Lets the user click the map to drop a submission pin.
function MapPicker({ active, onPick }) {
  useMapEvents({ click: (e) => { if (!active) return; const { lat, lng } = e.latlng.wrap(); onPick(lat, lng); } });
  return null;
}

// Some seeded descriptions carry citation artifacts like ":contentReference[oaicite:0]{index=0}"
const cleanYardText = (t) => String(t).replace(/:?contentReference\[[^\]]*\]\{[^}]*\}/g, "").replace(/\s+([.,])/g, "$1").trim();

// Cluster bubble: themed in App.css (.wreck-cluster) rather than the plugin's green/yellow/orange defaults.
const clusterIcon = (cluster) => {
  const n = cluster.getChildCount();
  const size = n < 10 ? "sm" : n < 100 ? "md" : "lg";
  return L.divIcon({
    html: `<span>${n}</span>`,
    className: `wreck-cluster wreck-cluster-${size}`,
    iconSize: L.point(40, 40),
  });
};

// Admin toast after a rejection. `notified` is false when migration 0005 hasn't been run yet.
const rejectedMessage = (what, data) =>
  data.notified === false
    ? `${what} rejected, but the contributor wasn't notified (run migration 0005 to turn notifications on).`
    : `${what} rejected and the contributor was notified.`;

function App() {
  const [theme, toggleTheme] = useTheme();
  const clusterRef = useRef(null);
  const [flyPosition, setFlyPosition] = useState(null);
  const [notification, setNotification] = useState("");
  // One left-hand panel at a time: "profile" | "favorites" | "submissions" | "submit" | "edit" | "leaderboard" | "details" | "user" | "changes" | "nearby" | null
  // /user/<name> opens that contributor's public profile straight away
  const [profileUser, setProfileUser] = useState(() => userFromPath(window.location.pathname));
  // /changes (or /changes?ship=12) opens the public recent-changes feed straight away
  const [changesShip, setChangesShip] = useState(() => changesFromPath(window.location.pathname, window.location.search)?.shipId || null);
  const [panel, setPanel] = useState(() => (
    userFromPath(window.location.pathname) ? "user"
      : changesFromPath(window.location.pathname, window.location.search) ? "changes"
      : null
  ));
  const [serverDupes, setServerDupes] = useState([]); // likely duplicates the server found (e.g. in the review queue)
  const [detailShipId, setDetailShipId] = useState(null);
  const [showPrivacy, setShowPrivacy] = useState(window.location.pathname === "/privacy");
  const [showTerms, setShowTerms] = useState(window.location.pathname === "/terms");
  const [showAbout, setShowAbout] = useState(window.location.pathname === "/about");
  const [showContact, setShowContact] = useState(window.location.pathname === "/contact");
  const [pickMode, setPickMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingFavs, setPendingFavs] = useState([]);
  const [showOptions, setShowOptions] = useState(false);
  const [showYards, setShowYards] = useState(false);
  const [showMarkers, setShowMarkers] = useState(true);
  const [mapStyle, setMapStyle] = useState("osm"); // default
  const [userLocation, setUserLocation] = useState(null);
  const [ships, refreshShips] = useShips(API_BASE);
  // map filters + timeline: `ships` stays the full list (search, latest, links); `visibleShips` is what the map draws
  const sf = useShipFilters(ships);
  const visibleShips = sf.visible;
  const [news, setNews] = useState([]);
  // Widgets
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [pendingShips, setPendingShips] = useState([]); 
  const [pendingReports, setPendingReports] = useState([]);
  const [pendingLoaded, setPendingLoaded] = useState(false);
  const [reportShip, setReportShip] = useState(null); // the wreck whose "Report this entry" dialog is open
  const lastRandomId = useRef(null); // so "Surprise me" doesn't repeat itself
  const [reviewBusy, setReviewBusy] = useState(null); // e.g. "ship-12" while that review request is running
  // Ship recycling / breaking yards (data comes from /api/yards)
  const [yards, setYards] = useState([]);
  const [loginData, setLoginData] = useState({ username: "", password: "" });
  const [signupData, setSignupData] = useState({
    username: "",
    password: "",
    email: "",
    name: "",
    country: "",
  });
  const [showSettings, setShowSettings] = useState(false);
  const [pendingEdits, setPendingEdits] = useState([]);
  const [editShip, setEditShip] = useState(null);
  const [editOriginal, setEditOriginal] = useState(null);
  const [editData, setEditData] = useState(EMPTY_SUBMIT);
  const [draftRestored, setDraftRestored] = useState(false);
  const [submitData, setSubmitData] = useState(() => {
    const d = loadDraft();
    return d || EMPTY_SUBMIT;
  });

  const [message, setMessage] = useState("");
  const [navOpen, setNavOpen] = useState(false); // phone layout: the left sidebar is a slide-in drawer

  const { account, logout, setAccount, validateSession } = useAuth(API_BASE);
  const mt = useMapTools({ ships: visibleShips, setMessage, setFlyPosition });
  const notifs = useNotifications(API_BASE, account);
  // Map clicks mean one thing at a time: placing a submission pin beats measuring, which beats the radius tool, which beats dropping a coordinate pin.
  const measureActive = mt.measureMode && !pickMode;
  const radiusActive = mt.radiusMode && !pickMode && !mt.measureMode;
  const coordsClickActive = mt.showCoordinates && !pickMode && !mt.measureMode && !mt.radiusMode;
  const coordsBarVisible = mt.showCoordinates || !!mt.pin;

  // Fetch news on mount
  useEffect(() => {
    fetch(`${API_BASE}/api/news`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setNews(data.slice(0, 5)); // latest 5 news items
      })
      .catch(err => console.error("Failed to fetch news:", err));
  }, []);


  // Load recycling yards the first time the layer is switched on
  useEffect(() => {
    if (!showYards || yards.length) return;
    fetch(`${API_BASE}/api/yards`)
      .then(res => res.json())
      .then(data => { if (Array.isArray(data)) setYards(data); })
      .catch(err => console.error("Failed to fetch recycling yards:", err));
  }, [showYards, yards.length]);

  useEffect(() => {
  if (message) {
    const timer = setTimeout(() => setMessage(""), 5000); // hide after 5s
    return () => clearTimeout(timer);
  }
}, [message]);


  // Remember the in-progress submission so closing the panel never loses work
  useEffect(() => {
    const empty = Object.keys(EMPTY_SUBMIT).every((k) => {
      const v = submitData[k];
      return v && typeof v === "object" ? Object.keys(v).length === 0 : !v;
    });
    try {
      if (empty) localStorage.removeItem(DRAFT_KEY);
      else localStorage.setItem(DRAFT_KEY, JSON.stringify(submitData));
    } catch {}
  }, [submitData]);

  useEffect(() => {
    if (loadDraft()) setDraftRestored(true);
  }, []);



  // log out => close any account panels
  // (the leaderboard, recent changes, nearby wrecks and ship details are public, so they stay open)
  useEffect(() => {
    if (!account) { setPanel((p) => (p === "leaderboard" || p === "details" || p === "user" || p === "changes" || p === "nearby" ? p : null)); setPickMode(false); setReportShip(null); }
  }, [account]);

  // ----- share links: /viewer/<slug> (or the older /<id>) -----
  const slugs = useMemo(() => buildSlugs(ships), [ships]);
  const markerRefs = useRef({});
  const linkHandled = useRef(false);

  // Fly to a ship; open its popup once the map arrives.
  const selectShip = (ship, { openPopup = true } = {}) => {
    if (sf.active && !visibleShips.some((x) => Number(x.id) === Number(ship.id))) {
      sf.reset(); // e.g. picked from search or a shared link while a filter hides it
      setMessage("Filters cleared so this wreck can be shown.");
    }
    setShowMarkers(true);
    setFlyPosition([ship.lat, ship.lng]);
    if (openPopup) {
      setTimeout(() => {
        const marker = markerRefs.current[ship.id];
        if (!marker) return;
        const group = clusterRef.current;
        // a marker inside a collapsed cluster isn't on the map yet: zoom/spiderfy to it, then open
        if (group?.zoomToShowLayer && group.hasLayer(marker) && !marker._map) group.zoomToShowLayer(marker, () => marker.openPopup());
        else marker.openPopup();
      }, 1700);
    }
  };

  const openFromPath = () => {
    const ref = shipRefFromPath(window.location.pathname);
    if (!ref) return;
    const ship = findShipByRef(ships, slugs, ref);
    if (ship) selectShip(ship);
    else setMessage("Sorry, we couldn't find that ship. It may have been renamed or removed.");
  };

  // first load: wait until ships arrive, then follow the link in the address bar
  useEffect(() => {
    if (linkHandled.current || ships.length === 0) return;
    linkHandled.current = true;
    openFromPath();
  }, [ships]); // eslint-disable-line react-hooks/exhaustive-deps

  const copyShareLink = async (ship) => {
    const url = shareUrl(slugs, ship);
    try {
      if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
        await navigator.share({ title: `${ship.title} — Open-Wrecks`, url });
      } else {
        await navigator.clipboard.writeText(url);
        setMessage("Link copied to clipboard!");
      }
    } catch (e) {
      if (e && e.name === "AbortError") return; // user dismissed the share sheet
      window.prompt("Copy this link:", url);
    }
  };

  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && setNavOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  // Phone layout: tapping an item in the drawer does its thing and then gets the drawer out of the way.
  const onSidebarClick = (e) => {
    if (e.target.closest(".widget, .submit-cta, .user-chip, .buttons button, .sidebar-footer a, .news-link")) setNavOpen(false);
  };

  // browser back/forward closes or opens the privacy policy, terms, about and contact pages
  useEffect(() => {
    const onPop = () => {
      const path = window.location.pathname;
      setShowPrivacy(path === "/privacy");
      setShowTerms(path === "/terms");
      setShowAbout(path === "/about");
      setShowContact(path === "/contact");
      const u = userFromPath(path);
      if (u) { setProfileUser(u); setPanel("user"); }
      else setPanel((p) => (p === "user" ? null : p));
      const c = changesFromPath(path, window.location.search);
      if (c) { setChangesShip(c.shipId); setPanel("changes"); }
      else setPanel((p) => (p === "changes" ? null : p));
      openFromPath();
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [ships, slugs]); // eslint-disable-line react-hooks/exhaustive-deps

  // the map pin / picker works on whichever form is open
  const editingNow = panel === "edit";
  const setFormData = editingNow ? setEditData : setSubmitData;
  const draftCoords = parseCoords((editingNow ? editData : submitData).coords);

  const handleDeleteAccount = () => {
  const session = Cookies.get("session");
  if (!session) return setMessage("No session found.");

  if (window.confirm("Are you sure you want to delete your account? This cannot be undone.\n\nShips and edits you submitted will stay on the site, with the author shown as \"deleted\".")) {
    fetch(`${API_BASE}/api/delete_account`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session })
    })
      .then(res => res.json())
      .then(data => {
        setMessage(data.message || data.error);
        if (!data.error) handleLogout();
      })
      .catch(err => setMessage("Failed to delete account: " + err));
  }
};

const pendingCount = pendingShips.length + pendingEdits.length + pendingReports.length;
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const pendingSummary = [pendingShips.length && plural(pendingShips.length, "ship"), pendingEdits.length && plural(pendingEdits.length, "edit"), pendingReports.length && plural(pendingReports.length, "report")].filter(Boolean).join(", ");
const favorites = (account?.favoriteShips || []).map(Number);
const shipIdSet = new Set(ships.map((x) => Number(x.id)));
const favoriteCount = favorites.filter((id) => shipIdSet.has(id)).length;

const toggleFavorite = (shipId) => {
  const session = Cookies.get("session");
  if (!session) return setMessage("Log in to save favorites.");
  const id = Number(shipId);
  if (pendingFavs.includes(id)) return;

  const before = account?.favoriteShips || [];
  const next = before.map(Number).includes(id)
    ? before.filter((f) => Number(f) !== id)
    : [...before, id];

  // optimistic update, rolled back if the server says no
  setPendingFavs((p) => [...p, id]);
  setAccount((prev) => (prev ? { ...prev, favoriteShips: next } : prev));

  fetch(`${API_BASE}/api/favorites`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session, ship_id: id }),
  })
    .then((res) => res.json())
    .then((data) => {
      if (data.error) throw new Error(data.error);
      setAccount((prev) => (prev ? { ...prev, favoriteShips: data.favorites } : prev));
    })
    .catch((e) => {
      setAccount((prev) => (prev ? { ...prev, favoriteShips: before } : prev));
      setMessage(e.message || "Failed to update favorites.");
    })
    .finally(() => setPendingFavs((p) => p.filter((x) => x !== id)));
};

const fetchPending = () => {
    const headers = { Authorization: `Bearer ${Cookies.get("session")}` };
    return Promise.all([
      fetch(`${API_BASE}/api/pending`, { headers })
        .then(res => res.json())
        .then(data => setPendingShips(Array.isArray(data) ? data : []))
        .catch(err => console.error("Failed to fetch pending:", err)),
      fetch(`${API_BASE}/api/edits`, { headers })
        .then(res => res.json())
        .then(data => setPendingEdits(Array.isArray(data) ? data : []))
        .catch(err => console.error("Failed to fetch edits:", err)),
      fetch(`${API_BASE}/api/reports`, { headers })
        .then(res => res.json())
        .then(data => setPendingReports(Array.isArray(data) ? data : []))
        .catch(err => console.error("Failed to fetch reports:", err)),
    ]).then(() => setPendingLoaded(true));
  };

  // Admins: load the review queue as soon as they're signed in (it feeds the sidebar badge) and keep it fresh.
  useEffect(() => {
    if (!account?.admin) { setPendingShips([]); setPendingEdits([]); setPendingReports([]); setPendingLoaded(false); return; }
    fetchPending();
    const timer = setInterval(fetchPending, 60000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.admin]);

  const reviewShip = async (id, action, extra = {}) => {
    if (reviewBusy) return;
    setReviewBusy(`ship-${id}`);
    try {
      const res = await fetch(`${API_BASE}/api/${action}/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session: Cookies.get("session"), ...extra }),
      });
      const data = await res.json();
      setMessage(data.error || (action === "approve" ? "Ship approved and added to the map." : rejectedMessage("Ship", data)));
      if (!data.error && action === "approve") refreshShips();
    } catch {
      setMessage("Review failed.");
    } finally {
      setReviewBusy(null);
      fetchPending();
    }
  };

  const reviewReport = async (id, extra = {}) => {
    if (reviewBusy) return;
    setReviewBusy(`report-${id}`);
    try {
      const res = await fetch(`${API_BASE}/api/reports/resolve/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session: Cookies.get("session"), ...extra }),
      });
      const data = await res.json();
      setMessage(data.error || `${data.message} The reporter was notified.`);
    } catch {
      setMessage("Review failed.");
    } finally {
      setReviewBusy(null);
      fetchPending();
    }
  };

  // "Report this entry": any signed-in user can flag a published wreck for the admins
  const openReport = (ship) => {
    if (!account) {
      setMessage("Log in to report an entry.");
      setShowLogin(true);
      return;
    }
    setReportShip(ship);
  };

  // resolves to { error? } so the dialog can show the reason inline; closes itself on success
  const submitReport = async (ship, { reason, note }) => {
    try {
      const res = await fetch(`${API_BASE}/api/report/${ship.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session: Cookies.get("session"), reason, note }),
      });
      const data = await res.json();
      if (data.error) return { error: data.error };
      setReportShip(null);
      setMessage(data.message);
      return {};
    } catch {
      return { error: "Couldn't reach the server. Please try again." };
    }
  };

  // "Surprise me": fly to a random wreck (one that matches the current filters, never the same one twice running)
  const surprise = () => {
    if (!ships.length) return setMessage("There are no wrecks on the map yet.");
    const ship = pickRandomShip(visibleShips, lastRandomId.current);
    if (!ship) return setMessage("No wrecks match your filters. Clear them to use Surprise me.");
    lastRandomId.current = ship.id;
    if (window.innerWidth <= 768) closePanel(); // on a phone a panel would cover the map
    selectShip(ship);
    setMessage(`🎲 ${ship.title}`);
  };

  // "Wrecks near me": the position stays in the browser; the map just shows where you are and flies there
  const openNearby = () => { setPickMode(false); setPanel("nearby"); };
  const onNearbyLocated = React.useCallback((latlng) => { setUserLocation(latlng); setFlyPosition(latlng); }, []);

  const handleShipClick = (ship) => selectShip(ship);
  const handleSubmitShip = (coords, images, links, { confirmDuplicate = false } = {}) => {
  const session = Cookies.get("session");
  if (!session) {
    setMessage("You must be logged in to submit.");
    return;
  }
  if (!coords) return setMessage("Please enter a valid location.");

  const { coords: _ignored, ...rest } = submitData;
  const payload = { session, ...rest, lat: coords.lat, lng: coords.lng, images, links, confirm_duplicate: confirmDuplicate };

  setSubmitting(true);
  fetch(`${API_BASE}/api/pending`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  })
    .then(res => res.json())
    .then(data => {
      if (data.error) {
        if (Array.isArray(data.duplicates)) setServerDupes(data.duplicates); // the form shows them and asks for confirmation
        return setMessage(data.error);
      }
      setMessage("Ship submitted for review. It will be on the map if approved.");
      setServerDupes([]);
      setSubmitData(EMPTY_SUBMIT);
      setDraftRestored(false);
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      setPickMode(false);
      setPanel("submissions"); // show it in the user's list straight away
    })
    .catch(err => setMessage("Failed to submit: " + err))
    .finally(() => setSubmitting(false));
};

  const openEdit = (ship) => {
    if (!account) {
      setMessage("Log in to suggest an edit.");
      setShowLogin(true);
      return;
    }
    const blank = (v) => (v === undefined || v === null || v === "unknown" || v === 0 ? "" : String(v));
    const data = {
      ...EMPTY_SUBMIT,
      title: ship.title || "",
      coords: `${ship.lat}, ${ship.lng}`,
      imo: blank(ship.imo), date: blank(ship.date), owner: blank(ship.owner),
      port_of_registry: blank(ship.port_of_registry), type: blank(ship.type), tonnage: blank(ship.tonnage),
      draught: blank(ship.draught), length: blank(ship.length), beam: blank(ship.beam),
      capacity: blank(ship.capacity), crew: blank(ship.crew), builder: blank(ship.builder),
      description: ship.description || "",
      images: (ship.images || []).join("\n"),
      image_meta: ship.image_meta || {},
      photo_attribution: ship.photo_attribution || "",
      links: (ship.links || []).join("\n"),
      note: "",
    };
    setEditShip(ship);
    setEditOriginal(data);
    setEditData(data);
    setPickMode(false);
    setPanel("edit");
  };

  const handleSubmitEdit = (coords, images, links) => {
    const session = Cookies.get("session");
    if (!session || !editShip || !coords) return;
    const { coords: _c, note, ...rest } = editData;
    setSubmitting(true);
    fetch(`${API_BASE}/api/suggest_edit/${editShip.id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session, note, updates: { ...rest, lat: coords.lat, lng: coords.lng, images, links } }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) return setMessage(data.error);
        setMessage(data.message);
        setPickMode(false);
        setEditShip(null);
        setPanel("submissions");
      })
      .catch((e) => setMessage("Failed to submit edit: " + e))
      .finally(() => setSubmitting(false));
  };

  const reviewEdit = async (id, action, extra = {}) => {
    const session = Cookies.get("session");
    try {
      const res = await fetch(`${API_BASE}/api/edits/${action}/${id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session, ...extra }),
      });
      const data = await res.json();
      setMessage(data.error || (action === "reject" ? rejectedMessage("Edit", data) : data.message));
      if (action === "approve" && !data.error) refreshShips(); // pick up the approved changes on the map
      fetchPending();
    } catch {
      setMessage("Review failed.");
    }
  };

  const openUser = (username) => {
    setProfileUser(username);
    setPickMode(false);
    setPanel("user");
    window.history.pushState({}, "", userPath(username));
  };

  // leaving the profile panel (any way) puts the address bar back on the map
  useEffect(() => {
    if (panel !== "user" && window.location.pathname.startsWith("/user/")) window.history.replaceState({}, "", "/");
  }, [panel]);

  const openChanges = (shipId = null) => {
    setChangesShip(shipId);
    setPickMode(false);
    setPanel("changes");
    window.history.pushState({}, "", changesPath(shipId));
  };
  const shareChanges = async (shipId) => {
    const url = changesShareUrl(shipId);
    try {
      await navigator.clipboard.writeText(url);
      setMessage("Link copied to clipboard!");
    } catch {
      window.prompt("Copy this link:", url);
    }
  };
  // leaving the changes panel (any way) puts the address bar back on the map
  useEffect(() => {
    if (panel !== "changes" && window.location.pathname.replace(/\/$/, "").toLowerCase() === "/changes") window.history.replaceState({}, "", "/");
  }, [panel]);

  const openDetails = (ship) => {
    setDetailShipId(Number(ship.id));
    setPickMode(false);
    setPanel("details");
  };
  const detailShip = detailShipId != null ? ships.find((x) => Number(x.id) === detailShipId) : null;

  const openSubmit = () => {
    if (!account) {
      setMessage("Log in or sign up to submit a shipwreck.");
      setShowLogin(true);
      return;
    }
    setPanel("submit");
  };

  const closePanel = () => {
    setPanel(null);
    setPickMode(false);
    setServerDupes([]);
  };

  const viewShipFromPanel = (ship) => {
    handleShipClick(ship);
    if (window.innerWidth <= 768) closePanel();
  };

  // Info pages (privacy, terms, about, contact): one open at a time, each has its own address.
  const openInfo = (which) => {
    setShowPrivacy(which === "privacy");
    setShowTerms(which === "terms");
    setShowAbout(which === "about");
    setShowContact(which === "contact");
    if (window.location.pathname !== `/${which}`) window.history.pushState({}, "", `/${which}`);
  };
  const closeInfo = (which) => {
    if (which === "privacy") setShowPrivacy(false);
    if (which === "terms") setShowTerms(false);
    if (which === "about") setShowAbout(false);
    if (which === "contact") setShowContact(false);
    if (window.location.pathname === `/${which}`) window.history.pushState({}, "", "/");
  };
  const openPrivacy = () => openInfo("privacy");
  const openTerms = () => openInfo("terms");
  const openAbout = () => openInfo("about");
  const openContact = () => openInfo("contact");
  const closePrivacy = () => closeInfo("privacy");
  const closeTerms = () => closeInfo("terms");
  const closeAbout = () => closeInfo("about");
  const closeContact = () => closeInfo("contact");

  const clearDraft = () => {
    setSubmitData(EMPTY_SUBMIT);
    setDraftRestored(false);
    try { localStorage.removeItem(DRAFT_KEY); } catch {}
  };

  const pickLocation = (lat, lng) => {
    setFormData((d) => ({ ...d, coords: formatCoords(lat, lng) }));
    setPickMode(false);
  };

  const requestUserLocation = (forSubmit = false) => {
  if (!navigator.geolocation) {
    setMessage("Geolocation is not supported by your browser.");
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (position) => {
      const { latitude, longitude } = position.coords;
      setUserLocation([latitude, longitude]);
      setFlyPosition([latitude, longitude]); // optional: fly to user
      if (forSubmit) setFormData((d) => ({ ...d, coords: formatCoords(latitude, longitude) }));
      setMessage("Your location has been found!");
    },
    (error) => {
      setMessage("Unable to retrieve your location: " + error.message);
    }
  );
};


  const handleLogin = () => {
    fetch(`${API_BASE}/api/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(loginData),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.session) {
          setSessionCookie(data.session);
          validateSession(data.session);
          setShowLogin(false);
        }
        setMessage(data.message || data.error);
      });
  };

  // returns the promise so the signup form knows when to refresh its captcha
  const handleSignup = (turnstile) =>
    fetch(`${API_BASE}/api/signup`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...signupData, turnstile }),
    })
      .then((res) => res.json())
      .then((data) => {
        setMessage(data.message || data.error);
        if (data.id) setShowSignup(false);
      })
      .catch(() => setMessage("Couldn't reach the server. Please try again."));

  const handleLogout = () => {
    const session = Cookies.get("session");
    // end the session on the server too, so a copied token stops working (fire-and-forget)
    if (session) {
      fetch(`${API_BASE}/api/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session }),
      }).catch(() => {});
    }
    Cookies.remove("session");
    setAccount(null);
  };

  return (
    <div className="app-wrapper">
      {message && <div className="notification">{message}</div>}

      {/* Topbar */}
<div className="topbar">
  <button
    type="button"
    className="nav-toggle"
    onClick={() => setNavOpen((o) => !o)}
    aria-label={navOpen ? "Close menu" : "Open menu"}
    aria-expanded={navOpen}
    aria-controls="app-sidebar"
  >
    <span aria-hidden="true">{navOpen ? "×" : "☰"}</span>
  </button>
  <h1>Open-Wrecks</h1>
  <span className="ship-counter">{sf.active ? `${visibleShips.length} of ${ships.length} wrecks shown` : `${ships.length} wrecks logged`}</span>
  <SearchBar ships={ships} onSelect={(ship) => { setNavOpen(false); handleShipClick(ship); }} />
  {account && (
    <button
      type="button"
      className="theme-toggle notif-bell"
      onClick={() => { setPickMode(false); setPanel(panel === "notifications" ? null : "notifications"); }}
      aria-label={notifs.unread ? `Notifications, ${notifs.unread} new` : "Notifications"}
      title="Notifications"
    >
      <span aria-hidden="true">🔔</span>
      {notifs.unread > 0 && <span className="notif-badge">{notifs.unread > 9 ? "9+" : notifs.unread}</span>}
    </button>
  )}
  <button
    type="button"
    className="theme-toggle"
    onClick={toggleTheme}
    aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
    title={theme === "dark" ? "Light mode" : "Dark mode"}
  >
    {theme === "dark" ? "☀" : "☾"}
  </button>
</div>


      <MobileNotice />

      <div className="app-container">
<MapTools
  showMarkers={showMarkers} onToggleMarkers={() => setShowMarkers(!showMarkers)}
  showYards={showYards} onToggleYards={() => setShowYards(!showYards)} yardCount={yards.length}
  filtersOpen={sf.filtersOpen} onToggleFilters={() => sf.setFiltersOpen(!sf.filtersOpen)} filtersActive={sf.active}
  timelineOpen={sf.timelineOpen} onToggleTimeline={() => sf.setTimelineOpen(!sf.timelineOpen)}
  showCoordinates={mt.showCoordinates} onToggleCoordinates={mt.toggleCoordinates}
  measureMode={mt.measureMode} onToggleMeasure={mt.toggleMeasure}
  canClearMeasure={mt.measurePoints.length > 0} onClearMeasure={mt.clearMeasure}
  gotoOpen={mt.gotoOpen} onToggleGoto={() => mt.setGotoOpen(!mt.gotoOpen)}
  radiusMode={mt.radiusMode} onToggleRadius={mt.toggleRadius}
  onFitAll={mt.fitAll}
  showScale={mt.showScale} onToggleScale={() => mt.setShowScale(!mt.showScale)}
  fullscreen={mt.fullscreen} onToggleFullscreen={mt.toggleFullscreen}
  onLocate={() => requestUserLocation()}
  nearbyOpen={panel === "nearby"} onToggleNearby={() => (panel === "nearby" ? closePanel() : openNearby())} onSurprise={surprise}
  mapStyle={mapStyle} mapStyles={mapStyles} onMapStyle={setMapStyle}
/>

        {/* Sidebar (a slide-in drawer on phones) */}
        {navOpen && <div className="sidebar-backdrop" onClick={() => setNavOpen(false)} />}
        <div id="app-sidebar" className={`sidebar${navOpen ? " open" : ""}`} onClick={onSidebarClick}>
          {/* User chip */}
          {account && (
            <button
              className={`user-chip${panel === "profile" ? " active" : ""}`}
              onClick={() => setPanel(panel === "profile" ? null : "profile")}
            >
              <span className="user-chip-avatar">{(account.name || account.username || "?").trim()[0].toUpperCase()}</span>
              <span className="user-chip-text">
                <strong>{account.name || account.username}</strong>
                <small>@{account.username}</small>
              </span>
              <span className="user-chip-caret">›</span>
            </button>
          )}

          {/* Always-visible submit button */}
          <button className="submit-cta" onClick={openSubmit}>
            <span>+</span> Submit a shipwreck
          </button>

          <div className={`widget${panel === "leaderboard" ? " widget-active" : ""}`} onClick={() => setPanel(panel === "leaderboard" ? null : "leaderboard")}>
            <img src="/boat.png" alt="Contributors" className="widget-icon" />
            <div><h4>Contributors</h4><p>Leaderboard &amp; points</p></div>
          </div>

          <div className={`widget${panel === "changes" ? " widget-active" : ""}`} onClick={() => (panel === "changes" ? closePanel() : openChanges())}>
            <img src="/boat.png" alt="Recent changes" className="widget-icon" />
            <div><h4>Recent changes</h4><p>New wrecks &amp; approved edits</p></div>
          </div>

          <div className={`widget${panel === "nearby" ? " widget-active" : ""}`} onClick={() => (panel === "nearby" ? closePanel() : openNearby())}>
            <img src="/boat.png" alt="Wrecks near me" className="widget-icon" />
            <div><h4>Wrecks near me</h4><p>Closest wrecks to your location</p></div>
          </div>

          <div className="widget" onClick={surprise}>
            <img src="/boat.png" alt="Surprise me" className="widget-icon" />
            <div><h4>Surprise me</h4><p>Jump to a random wreck</p></div>
          </div>

        <SidebarCarousel
  ships={[...ships].sort((a, b) => b.id - a.id).slice(0, 6)}
  onShipClick={handleShipClick}
/>

         {/* News Section */}
<div className="news-section">
  <h2>Latest News</h2>
  {news.length === 0 && <p>No news available</p>}
  {news.slice(0, 3).map((item, i) => (
    <div key={i} className="news-card">
      <div className="news-header">
        <h4>{item.title}</h4>
        <span className={`news-severity news-${item.severity}`}>
          {item.severity.toUpperCase()}
        </span>
      </div>
      <div className="news-divider"></div>
      {item.image && item.image.trim() !== "" && (
        <img src={item.image} alt={item.title} className="news-img" />
      )}
      {item.summary && <p className="news-summary">{item.summary}</p>}
      {item.link && (
        <a href={item.link} target="_blank" rel="noopener noreferrer" className="news-link">
          Read more
        </a>
      )}
    </div>
  ))}
</div>


 {!account && (
    <div className="buttons">
      <button onClick={() => setShowLogin(true)}>Login</button>
      <button onClick={() => setShowSignup(true)}>Signup</button>
    </div>
  )}

  {/* Admin section: review queue + user management */}
  {account?.admin && (
    <div className="admin-section">
      <h2>Admin</h2>
      <div
        className={`widget widget-pending${panel === "pending" ? " widget-active" : ""}${pendingCount ? " has-pending" : ""}`}
        onClick={() => { if (panel !== "pending") fetchPending(); setPanel(panel === "pending" ? null : "pending"); }}
      >
        <img src="/boat.png" alt="Pending Ships" className="widget-icon" />
        <div>
          <h4>Pending Ships</h4>
          <p>{!pendingLoaded ? "Checking the queue…" : pendingCount ? `${pendingSummary} to review` : "Nothing waiting"}</p>
        </div>
        {pendingCount > 0 && <span className="widget-badge" aria-label={`${pendingCount} pending`}>{pendingCount}</span>}
      </div>
      <div className={`widget${panel === "users" ? " widget-active" : ""}`} onClick={() => setPanel(panel === "users" ? null : "users")}>
        <img src="/boat.png" alt="Manage Users" className="widget-icon" />
        <div><h4>Manage Users</h4><p>Disable or enable accounts</p></div>
      </div>
    </div>
  )}

  {/* Account section */}
  {account && (
    <div className="admin-section">
      <h2>Account</h2>

      <div className={`widget${panel === "profile" ? " widget-active" : ""}`} onClick={() => setPanel(panel === "profile" ? null : "profile")}>
        <img src="/boat.png" alt="Profile" className="widget-icon" />
        <div><h4>Profile</h4><p>Your info and stats</p></div>
      </div>
      <div className={`widget${panel === "favorites" ? " widget-active" : ""}`} onClick={() => setPanel(panel === "favorites" ? null : "favorites")}>
        <img src="/boat.png" alt="Favorites" className="widget-icon" />
        <div><h4>Favorites</h4><p>{favoriteCount} saved ship{favoriteCount === 1 ? "" : "s"}</p></div>
      </div>
      <div className={`widget${panel === "submissions" ? " widget-active" : ""}`} onClick={() => setPanel(panel === "submissions" ? null : "submissions")}>
        <img src="/boat.png" alt="My Submissions" className="widget-icon" />
        <div><h4>My Submissions</h4><p>Check the status of your wrecks</p></div>
      </div>

      <div className="widget" onClick={() => setShowSettings(true)}>
        <img src="/boat.png" alt="Settings" className="widget-icon" />
        <div><h4>Settings</h4><p>Password &amp; account</p></div>
      </div>
      <div className="widget" onClick={handleLogout}>
        <img src="/boat.png" alt="Logout" className="widget-icon" />
        <div><h4>Logout</h4><p>Sign out of your account</p></div>
      </div>
    </div>
  )}

  <footer className="sidebar-footer">
    <nav className="footer-links" aria-label="Site information">
      <a href="/about" onClick={(e) => { e.preventDefault(); openAbout(); }}>About</a>
      <a href="/contact" onClick={(e) => { e.preventDefault(); openContact(); }}>Contact</a>
      <a href="/privacy" onClick={(e) => { e.preventDefault(); openPrivacy(); }}>Privacy Policy</a>
      <a href="/terms" onClick={(e) => { e.preventDefault(); openTerms(); }}>Terms and Conditions</a>
    </nav>
    <div className="footer-data">
      Open data: <a href="/api/export?format=geojson">GeoJSON</a> / <a href="/api/export?format=csv">CSV</a>
    </div>
  </footer>
</div>

        {/* Map */}
        <div ref={mt.containerRef} className={`map-container${pickMode ? " pick-mode" : ""}${measureActive || radiusActive ? " measuring" : ""}`}>
          <MapContainer
            center={ships.length ? [ships[0].lat, ships[0].lng] : [0, 0]}
            zoom={3}
            minZoom={2}
            style={{ height: "100%", width: "100%" }}
          >
           <TileLayer
  url={mapStyles[mapStyle].url}
  attribution={mapStyles[mapStyle].attribution}
/>

            <MapBridge mapRef={mt.mapRef} />
            {mt.showScale && <ScaleControl position="bottomleft" />}
            <MapPicker active={pickMode} onPick={pickLocation} />
            <CoordinatesTracker active={coordsClickActive} track={coordsBarVisible && !mt.pin} format={mt.coordsFormat} outRef={mt.coordsOutRef} onPin={mt.setPin} />
            <PinMarker pin={mt.pin} format={mt.coordsFormat} />
            <RadiusLayer active={radiusActive} center={mt.radiusCenter} radiusM={mt.radiusM} onPick={mt.pickRadiusCenter} />
            {(panel === "submit" || panel === "edit") && draftCoords && (
              <Marker
                position={[draftCoords.lat, draftCoords.lng]}
                icon={homeIcon}
                draggable
                eventHandlers={{
                  dragend: (e) => {
                    const { lat, lng } = e.target.getLatLng();
                    setFormData((d) => ({ ...d, coords: formatCoords(lat, lng) }));
                  },
                }}
              >
                <Tooltip permanent direction="top" offset={[0, -30]}>{editingNow ? "New location" : "New wreck location"}</Tooltip>
              </Marker>
            )}

            {flyPosition && <FlyToPosition position={flyPosition} zoom={6} />}
            {userLocation && (
  <Marker position={userLocation} icon={homeIcon}>
    <Popup>
      <strong>You are here!</strong>
    </Popup>
  </Marker>
)}

            {showMarkers && (
            <MarkerClusterGroup
              ref={clusterRef}
              chunkedLoading
              showCoverageOnHover={false}
              maxClusterRadius={50}
              iconCreateFunction={clusterIcon}
            >
  {visibleShips.map((ship) => (
    <Marker
      key={ship.id}
      position={[ship.lat, ship.lng]}
      icon={boatIcon}
      ref={(m) => { if (m) markerRefs.current[ship.id] = m; else delete markerRefs.current[ship.id]; }}
      eventHandlers={{
  click: (e) => {
    if (measureActive) {
      mt.addMeasurePoint([ship.lat, ship.lng]);
      e.target.closePopup(); // measuring: snap to the wreck without opening its popup
    } else {
      selectShip(ship, { openPopup: false }); // the click already opens the popup
    }
  },
  // keep the address bar on a shareable link for whichever ship is open
  popupopen: () => {
    window.history.replaceState({}, "", `/viewer/${slugs.get(Number(ship.id))}`);
    document.title = `${ship.title} — Open-Wrecks`;
  },
  popupclose: () => {
    if (window.location.pathname === `/viewer/${slugs.get(Number(ship.id))}`) {
      window.history.replaceState({}, "", "/");
      document.title = "Open-Wrecks — a community map of shipwrecks";
    }
  },
}}
>
      <Popup maxWidth={320}>
        <h3>{ship.title}</h3>
        <p className="credit-line">Boat ID: {ship.id}</p>
        {ship.submitted_by && (
          <p className="credit-line">
            Submitted by <UserLink username={ship.submitted_by} onOpen={openUser} />
            {ship.edited_by?.length > 0 && <> · Edits by {ship.edited_by.map((u, i) => <React.Fragment key={u}>{i > 0 && ", "}<UserLink username={u} onOpen={openUser} /></React.Fragment>)}</>}
          </p>
        )}
        <div className="popup-actions">
          {account && (
            <button
              className={`fav-btn${favorites.includes(Number(ship.id)) ? " on" : ""}`}
              disabled={pendingFavs.includes(Number(ship.id))}
              onClick={() => toggleFavorite(ship.id)}
            >
              {favorites.includes(Number(ship.id)) ? "★ Favorited" : "☆ Favorite"}
            </button>
          )}
          <button className="edit-btn share-btn" onClick={() => copyShareLink(ship)}>🔗 Share</button>
        </div>
        {ship.images && ship.images.length > 0 && <ImageCarousel images={ship.images} meta={ship.image_meta} />}
        {(() => {
          const meta = [ship.type, ship.date].filter((v) => v && v !== "unknown");
          const p = preview(ship.description, 220);
          return (
            <>
              {meta.length > 0 && <p className="popup-meta">{meta.join(" · ")}</p>}
              {p.text && <p className="popup-preview">{p.text}</p>}
              {(() => {
                const gaps = needsWorkReasons(ship);
                return gaps.length > 0 && <p className="popup-needs-work">⚠ Needs work: {gaps.join(" · ")}. Press ✎ Suggest edit to improve it.</p>;
              })()}
            </>
          );
        })()}
        <div className="popup-actions">
          <button className="edit-btn popup-details-btn" onClick={() => openDetails(ship)}>📖 Full details</button>
          <button className="edit-btn" onClick={() => openEdit(ship)}>✎ Suggest edit</button>
          <button className="edit-btn report-btn" onClick={() => openReport(ship)} title="Tell the admins something is wrong with this entry">⚑ Report</button>
        </div>
        <div style={{ marginTop: "10px" }}>
  {ship.links && ship.links.slice(0, 2).map((link, i) => (
    <div key={i} className="popup-link">
      <a href={link} target="_blank" rel="noopener noreferrer">{link.replace(/^https?:\/\/(www\.)?/, "")}</a>
    </div>
  ))}
  {ship.links && ship.links.length > 2 && <div className="popup-link"><button className="panel-link" onClick={() => openDetails(ship)}>+{ship.links.length - 2} more links</button></div>}
  <div>
    <a
      href={`https://www.google.com/maps?q=${ship.lat},${ship.lng}`}
      target="_blank"
      rel="noopener noreferrer"
      className="map-link"
    >
      View on Google Maps
    </a>
  </div>
</div>
      {mt.showCoordinates && (
        <p className="popup-coords">
          📍 {formatLatLng(ship.lat, ship.lng, mt.coordsFormat)}{" "}
          <button type="button" onClick={() => mt.copyLatLng(ship.lat, ship.lng)} title="Copy coordinates">⧉</button>
        </p>
      )}
      </Popup>
    </Marker>
  ))}
            </MarkerClusterGroup>
            )}
        {showYards && yards.map((yard) => (
  <Marker
    key={`yard-${yard.id}`}
    position={[yard.lat, yard.lng]}
    icon={yardIcon}
    eventHandlers={{
      click: (e) => { if (measureActive) { mt.addMeasurePoint([yard.lat, yard.lng]); e.target.closePopup(); } },
    }}
  >
    <Popup maxWidth={320} className="yard-popup">
      <h3>{yard.name}</h3>
      {yard.country && <p className="yard-country">{yard.country} · Ship recycling</p>}
      {yard.images && yard.images.length > 0 && <ImageCarousel images={yard.images} />}
      {yard.description && (
        <div style={{ marginTop: "10px" }}>
          <ReactMarkdown>{cleanYardText(yard.description)}</ReactMarkdown>
        </div>
      )}
      <div style={{ marginTop: "10px" }}>
        {yard.links && yard.links.map((link, i) => (
          <div key={i} className="popup-link">
            <a href={link} target="_blank" rel="noopener noreferrer">{link.replace(/^https?:\/\/(www\.)?/, "")}</a>
          </div>
        ))}
        <div>
          <a href={`https://www.google.com/maps?q=${yard.lat},${yard.lng}`} target="_blank" rel="noopener noreferrer" className="map-link">
            View on Google Maps
          </a>
        </div>
      </div>
      {mt.showCoordinates && (
        <p className="popup-coords">
          📍 {formatLatLng(yard.lat, yard.lng, mt.coordsFormat)}{" "}
          <button type="button" onClick={() => mt.copyLatLng(yard.lat, yard.lng)} title="Copy coordinates">⧉</button>
        </p>
      )}
    </Popup>
  </Marker>
))}
<MeasureLayer
  points={mt.measurePoints}
  active={measureActive}
  unit={mt.unit}
  onAdd={mt.addMeasurePoint}
  onMove={mt.moveMeasurePoint}
  onRemove={mt.removeMeasurePoint}
/>

          </MapContainer>

{sf.filtersOpen && (
  <FilterPanel
    ships={ships}
    visibleCount={visibleShips.length}
    filters={sf.filters}
    bounds={sf.bounds}
    active={sf.active}
    onToggleType={sf.toggleType}
    onPatch={sf.patch}
    onReset={sf.reset}
    onShipClick={(ship) => { sf.setFiltersOpen(window.innerWidth > 768); selectShip(ship); }}
    onOpenTimeline={() => sf.setTimelineOpen(true)}
    onClose={() => sf.setFiltersOpen(false)}
  />
)}
{sf.timelineOpen && (
  <TimelineBar
    ships={ships}
    bounds={sf.bounds}
    from={sf.filters.from}
    to={sf.filters.to}
    includeUndated={sf.filters.includeUndated}
    shifted={!!panel}
    onRange={sf.setRange}
    onToggleUndated={(v) => sf.patch({ includeUndated: v })}
    onClear={() => sf.patch({ from: null, to: null })}
    onClose={() => sf.setTimelineOpen(false)}
  />
)}
          
{pickMode && (panel === "submit" || panel === "edit") && (
  <div className="pick-banner">Tap or click the map to set the location · <button onClick={() => setPickMode(false)}>Cancel</button></div>
)}

<div className="map-tool-stack">
<MeasurePanel
  active={measureActive}
  points={mt.measurePoints}
  unit={mt.unit}
  onUnit={mt.setUnit}
  onUndo={mt.undoMeasure}
  onClear={mt.clearMeasure}
  onCopy={mt.copyMeasurement}
  onDone={() => mt.setMeasureMode(false)}
  onResume={mt.toggleMeasure}
/>
{mt.gotoOpen && <GoToBox onGo={mt.goTo} onClose={() => mt.setGotoOpen(false)} />}
{mt.radiusMode && (
  <RadiusPanel
    center={mt.radiusCenter}
    unit={mt.unit}
    onUnit={mt.setUnit}
    value={mt.radiusValue}
    onValue={mt.setRadiusValue}
    radiusM={mt.radiusM}
    tooBig={mt.radiusTooBig}
    results={mt.radiusResults}
    onMyLocation={mt.radiusFromMyLocation}
    onFit={() => mt.fitCircle(mt.radiusCenter, mt.radiusM)}
    onSelectShip={handleShipClick}
    onClose={mt.closeRadius}
  />
)}
</div>
<CoordinatesBar
  visible={coordsBarVisible}
  outRef={mt.coordsOutRef}
  format={mt.coordsFormat}
  onFormat={mt.setCoordsFormat}
  pin={mt.pin}
  onClearPin={() => mt.setPin(null)}
  onCopy={mt.copyCoordinates}
/>

{panel === "nearby" && (
  <NearbyPanel
    ships={visibleShips}
    filtersActive={sf.active}
    point={userLocation}
    unit={mt.unit}
    onUnit={mt.setUnit}
    onLocated={onNearbyLocated}
    onSelect={viewShipFromPanel}
    onSurprise={surprise}
    onClose={closePanel}
  />
)}
{panel === "leaderboard" && <Leaderboard API_BASE={API_BASE} onClose={closePanel} onOpenUser={openUser} />}
{panel === "changes" && (
  <RecentChanges
    API_BASE={API_BASE}
    account={account}
    ships={ships}
    shipId={changesShip}
    onClearShip={() => openChanges(null)}
    onShowHistory={openChanges}
    onClose={closePanel}
    onViewShip={viewShipFromPanel}
    onOpenUser={openUser}
    onShare={shareChanges}
    onReverted={refreshShips}
    setMessage={setMessage}
  />
)}
{panel === "user" && profileUser && (
  <UserProfile
    API_BASE={API_BASE}
    username={profileUser}
    account={account}
    ships={ships}
    onClose={closePanel}
    onViewShip={viewShipFromPanel}
    onBioSaved={(bio) => setAccount((prev) => (prev ? { ...prev, bio: bio || undefined } : prev))}
    setMessage={setMessage}
  />
)}
{panel === "details" && detailShip && (
  <ShipDetails
    ship={detailShip}
    account={account}
    isFavorite={favorites.includes(Number(detailShip.id))}
    favoritePending={pendingFavs.includes(Number(detailShip.id))}
    onClose={closePanel}
    onShowOnMap={() => { selectShip(detailShip); if (window.innerWidth <= 768) closePanel(); }}
    onToggleFavorite={() => toggleFavorite(detailShip.id)}
    onShare={() => copyShareLink(detailShip)}
    onEdit={() => openEdit(detailShip)}
    onReport={() => openReport(detailShip)}
    onHistory={() => openChanges(Number(detailShip.id))}
    onOpenUser={openUser}
  />
)}
{account && panel === "profile" && (
  <ProfileCard
    API_BASE={API_BASE}
    account={account}
    favoriteCount={favoriteCount}
    onClose={closePanel}
    onOpenFavorites={() => setPanel("favorites")}
    onOpenLeaderboard={() => setPanel("leaderboard")}
    onOpenPublicProfile={() => openUser(account.username)}
    onOpenSubmissions={() => setPanel("submissions")}
    onOpenSettings={() => setShowSettings(true)}
    onOpenPrivacy={openPrivacy}
    onOpenTerms={openTerms}
    onLogout={handleLogout}
  />
)}
{account && panel === "favorites" && (
  <FavoritesPanel
    ships={ships}
    favorites={favorites}
    pendingIds={pendingFavs}
    onShipClick={viewShipFromPanel}
    onToggle={toggleFavorite}
    onClose={closePanel}
  />
)}
{account?.admin && panel === "pending" && (
  <PendingPanel
    ships={pendingShips}
    edits={pendingEdits}
    reports={pendingReports}
    mapShips={ships}
    loaded={pendingLoaded}
    busyId={reviewBusy}
    onRefresh={fetchPending}
    onReviewShip={reviewShip}
    onReviewEdit={reviewEdit}
    onReviewReport={reviewReport}
    onViewShip={viewShipFromPanel}
    onOpenUser={openUser}
    onClose={closePanel}
  />
)}
{account?.admin && panel === "users" && (
  <UserAdmin API_BASE={API_BASE} account={account} onClose={closePanel} setMessage={setMessage} />
)}
{account && panel === "notifications" && (
  <NotificationsPanel
    items={notifs.items}
    unread={notifs.unread}
    ships={ships}
    onMarkRead={notifs.markRead}
    onViewShip={viewShipFromPanel}
    onOpenSubmissions={() => setPanel("submissions")}
    onClose={closePanel}
  />
)}
{account && panel === "submissions" && (
  <MySubmissions
    API_BASE={API_BASE}
    ships={ships}
    onClose={closePanel}
    onViewShip={viewShipFromPanel}
    onNewSubmission={() => setPanel("submit")}
  />
)}
{account && panel === "edit" && editShip && (
  <SubmitWidget
    mode="edit"
    shipTitle={editShip.title}
    original={editOriginal}
    submitData={editData}
    setSubmitData={setEditData}
    handleSubmitShip={handleSubmitEdit}
    onClose={closePanel}
    submitting={submitting}
    pickMode={pickMode}
    setPickMode={setPickMode}
    onUseMyLocation={() => requestUserLocation(true)}
  />
)}
{account && panel === "submit" && (
  <SubmitWidget
    submitData={submitData}
    setSubmitData={setSubmitData}
    handleSubmitShip={handleSubmitShip}
    onClose={closePanel}
    submitting={submitting}
    pickMode={pickMode}
    setPickMode={setPickMode}
    onUseMyLocation={() => requestUserLocation(true)}
    hasDraft={draftRestored}
    onClearDraft={clearDraft}
    ships={ships}
    onShowShip={handleShipClick}
    extraDuplicates={serverDupes}
  />
)}

        </div>
      </div>
      {/* Login Widget */}
{showLogin && !account && (
  <LoginWidget
    loginData={loginData}
    setLoginData={setLoginData}
    handleLogin={handleLogin}
    setShowLogin={setShowLogin}
    message={message}
  />
)}
{showSettings && account && (
  <SettingsWidget
    API_BASE={API_BASE}
    onClose={() => setShowSettings(false)}
    onDeleteAccount={handleDeleteAccount}
  />
)}
     {/* Signup Widget */}
{showSignup && !account && (
  <SignupWidget
    signupData={signupData}
    setSignupData={setSignupData}
    handleSignup={handleSignup}
    setShowSignup={setShowSignup}
    onShowPrivacy={openPrivacy}
    onShowTerms={openTerms}
    message={message}
  />
)}

      {reportShip && account && (
        <ReportDialog
          ship={reportShip}
          onSubmit={(r) => submitReport(reportShip, r)}
          onClose={() => setReportShip(null)}
          onSuggestEdit={() => { const s = reportShip; setReportShip(null); openEdit(s); }}
          onOpenContact={() => { setReportShip(null); openContact(); }}
        />
      )}
      {showPrivacy && <PrivacyPolicy onClose={closePrivacy} />}
      {showTerms && <TermsAndConditions onClose={closeTerms} />}
      {showAbout && <AboutPage onClose={closeAbout} shipCount={ships.length} onOpenContact={openContact} onOpenPrivacy={openPrivacy} onOpenTerms={openTerms} />}
      {showContact && <ContactPage onClose={closeContact} onOpenPrivacy={openPrivacy} />}
    </div>
  );
}

export default App;
